import React, { useState } from 'react';
import {
  Award,
  Calendar,
  Clock,
  User,
  Video,
  FileText,
  CheckCircle2,
  Play,
  Plus,
  Search,
  Filter,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { PracticeConsultation, ConsultationSessionStatus, PracticeCase } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { formatDateTime } from '../../utils/dateUtils';

interface ConsultationManagerProps {
  consultations: PracticeConsultation[];
  lang: Language;
  onStartConsultation: (session: PracticeConsultation) => void;
  onOpenReportForConsultation: (session: PracticeConsultation) => void;
  onNewConsultation: () => void;
  onUpdateStatus: (id: string, status: ConsultationSessionStatus) => void;
}

const STATUS_CONFIG: Record<
  ConsultationSessionStatus,
  { labelEn: string; labelHi: string; color: string }
> = {
  SCHEDULED: { labelEn: 'Scheduled', labelHi: 'नियत', color: 'bg-blue-500/15 text-blue-800 dark:text-blue-300' },
  IN_PROGRESS: { labelEn: 'In Progress', labelHi: 'प्रगति पर', color: 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300' },
  COMPLETED: { labelEn: 'Completed', labelHi: 'संपन्न', color: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300' },
  REPORT_PENDING: { labelEn: 'Report Pending', labelHi: 'रिपोर्ट लंबित', color: 'bg-purple-500/15 text-purple-800 dark:text-purple-300' },
  DELIVERED: { labelEn: 'Delivered', labelHi: 'वितरित', color: 'bg-teal-500/15 text-teal-800 dark:text-teal-300' },
  FOLLOW_UP_DUE: { labelEn: 'Follow-Up Due', labelHi: 'फॉलो-अप देय', color: 'bg-rose-500/15 text-rose-800 dark:text-rose-300' }
};

export const ConsultationManager: React.FC<ConsultationManagerProps> = ({
  consultations,
  lang,
  onStartConsultation,
  onOpenReportForConsultation,
  onNewConsultation,
  onUpdateStatus
}) => {
  const isHi = lang === 'hi';
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredConsultations = consultations.filter((con) => {
    const matchSearch =
      con.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      con.consultationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      con.question.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'ALL' || con.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isHi ? 'सत्र, क्लाइंट या विषय खोजें...' : 'Search session, client or question...'}
              className="w-full pl-9 pr-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="ALL">{isHi ? 'सभी सत्र' : 'All Sessions'}</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="REPORT_PENDING">Report Pending</option>
          </select>
        </div>

        <button
          onClick={onNewConsultation}
          className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>{isHi ? '+ नया सत्र नियत करें' : '+ Schedule Session'}</span>
        </button>
      </div>

      {/* Consultations List */}
      <div className="space-y-3">
        {filteredConsultations.map((session) => {
          const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.SCHEDULED;
          const dateFormatted = formatDateTime(session.scheduledDateTime);

          return (
            <div
              key={session.id}
              className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              {/* Left Details */}
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-xs text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.5 rounded border border-[#E2DED5] dark:border-amber-800">
                    {session.consultationNumber}
                  </span>
                  <span className="font-bold text-sm text-astro-text dark:text-astro-muted">
                    {session.clientName}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusConf.color}`}>
                    {isHi ? statusConf.labelHi : statusConf.labelEn}
                  </span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                    {session.meetingFormat} ({session.durationMinutes} min)
                  </span>
                </div>

                <div className="text-xs text-astro-secondary-text dark:text-astro-muted italic bg-astro-secondary-surface dark:bg-astro-primary p-2 rounded-lg border border-astro-border dark:border-astro-strong-border">
                  "{session.question}"
                </div>

                <div className="flex items-center gap-4 text-xs text-astro-muted dark:text-astro-muted pt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-astro-muted" />
                    <strong>Time:</strong> {dateFormatted}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-astro-muted" />
                    {session.assignedPractitioner}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => onStartConsultation(session)}
                  className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <Award className="w-4 h-4" />
                  <span>
                    {session.status === 'SCHEDULED'
                      ? isHi ? 'परामर्श प्रारंभ करें' : 'Start Acharya Session'
                      : isHi ? 'सत्र खोलें' : 'Resume Session'}
                  </span>
                </button>

                {session.reportId ? (
                  <button
                    onClick={() => onOpenReportForConsultation(session)}
                    className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 border border-indigo-200 dark:border-indigo-800 transition-colors cursor-pointer"
                    title="View Report"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => onOpenReportForConsultation(session)}
                    className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
                    title="Create Report"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                )}

                {session.status !== 'COMPLETED' && (
                  <button
                    onClick={() => onUpdateStatus(session.id, 'COMPLETED')}
                    className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800 transition-colors cursor-pointer"
                    title="Mark Completed"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filteredConsultations.length === 0 && (
          <div className="p-12 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 text-xs text-astro-muted">
            No consultations scheduled in this filter.
          </div>
        )}
      </div>
    </div>
  );
};
